<template>
  <template v-if="items.length">
    <div class="dkn-admin-statuses mt-2">
      <div
        v-for="status in quickStatuses"
        :key="status.key"
        class="dkn-admin-status"
        :class="status.toneClass"
      >
        <div class="dkn-admin-status-header">
          <i :class="status.icon" />
          <span>{{ status.label }}</span>
        </div>
        <div class="dkn-admin-status-value">
          {{ status.value }}
        </div>
      </div>
    </div>

    <div class="asset-stats-grid">
      <div
        v-for="item in items"
        :key="item.key"
        class="dkn-admin-status dkn-param-card"
        :class="item.toneClass"
      >
        <div class="dkn-admin-status-header">
          <i class="bi bi-snow" />
          <span>{{ item.label }}</span>
        </div>
        <div class="dkn-admin-status-value">
          {{ item.value }}
        </div>
      </div>
    </div>
  </template>
  <div
    v-else
    class="modbus-empty"
  >
    <i class="bi bi-thermometer-snow" />
    <span>Brak danych chłodni</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

const DKN_PREFIX = 'DKN_LMS_'
type Tone = 'neutral' | 'ok' | 'warn' | 'bad'
const QUICK_STATUS_KEYS = new Set([
  'DKN_LMS_ALR',
  'DKN_LMS_DFS',
  'DKN_LMS_VSR',
  'DKN_LMS_SH',
  'DKN_LMS_PPU'
])

function toNumber(raw: any): number | null {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const parsed = parseFloat(String(raw))
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeValue(raw: any): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  if (typeof raw === 'number') return Number.isInteger(raw) ? raw.toString() : raw.toFixed(2)
  const parsed = parseFloat(raw)
  if (Number.isFinite(parsed)) {
    return Number.isInteger(parsed) ? parsed.toString() : parsed.toFixed(2)
  }
  return String(raw)
}


function normalizeUnit(unit: string | null): string | null {
  if (!unit) return unit
  const trimmed = unit.trim()
  if (trimmed === 'C') return '°C'
  return trimmed
}
function extractUnit(description?: string | null): string | null {
  if (!description) return null
  const match = description.match(/\(([^)]+)\)\s*$/)
  return match ? match[1] : null
}

function stripUnit(description?: string | null): string | null {
  if (!description) return null
  return description.replace(/\s*\([^)]*\)\s*$/, '').trim()
}

function toneClass(tone: Tone): string {
  return `dkn-tone-${tone}`
}

function getParamTone(name: string, rawValue: any): Tone {
  const value = toNumber(rawValue)
  if (value === null) return 'neutral'

  if (name === 'DKN_LMS_ALR') return value > 0 ? 'bad' : 'ok'
  if (name === 'DKN_LMS_DFS') return value > 0 ? 'warn' : 'ok'
  if (name === 'DKN_LMS_VSR') {
    if (value > 90) return 'bad'
    if (value > 70) return 'warn'
    return 'ok'
  }
  if (name === 'DKN_LMS_SH' || name === 'DKN_LMS_SHB') {
    if (value > 25) return 'bad'
    if (value > 15 || value < 1) return 'warn'
    return 'ok'
  }
  if (name === 'DKN_LMS_PPU' || name === 'DKN_LMS_PPUB') {
    if (value >= 100) return 'bad'
    if (value >= 95) return 'warn'
    return 'ok'
  }
  if (name === 'DKN_LMS_SDT' || name === 'DKN_LMS_SDTB') {
    if (value > 90) return 'bad'
    if (value > 70) return 'warn'
    return 'ok'
  }
  if (name.startsWith('DKN_LMS_') && ['DKN_LMS_SV', 'DKN_LMS_SC', 'DKN_LMS_SCB', 'DKN_LMS_TGS', 'DKN_LMS_TGSB', 'DKN_LMS_TEU', 'DKN_LMS_TEUB'].includes(name)) {
    if (value > 80 || value < -30) return 'bad'
    if (value > 50 || value < -10) return 'warn'
    return 'ok'
  }

  return 'neutral'
}

export default defineComponent({
  name: 'DknLmsLatestParamsPanel',
  props: {
    latestParams: {
      type: Array,
      required: true
    }
  },
  computed: {
    dknParamsByName(): Record<string, any> {
      const params = Array.isArray(this.latestParams) ? this.latestParams : []
      const seenNames = new Set<string>()
      const rows = params
        .filter((param: any) => {
          const name = param?.can_params_io?.param_name
          return typeof name === 'string' && name.startsWith(DKN_PREFIX)
        })
        .filter((param: any) => {
          const name = param?.can_params_io?.param_name
          if (!name || seenNames.has(name)) return false
          seenNames.add(name)
          return true
        })
      return rows.reduce((acc: Record<string, any>, param: any) => {
        const name = param?.can_params_io?.param_name
        if (typeof name === 'string') acc[name] = param
        return acc
      }, {})
    },
    quickStatuses(): Array<{ key: string; label: string; value: string; icon: string; toneClass: string }> {
      const build = (
        key: string,
        label: string,
        icon: string,
        valueFormatter?: (raw: any) => string
      ) => {
        const row = this.dknParamsByName[key]
        const raw = row?.value
        const tone = getParamTone(key, raw)
        return {
          key,
          label,
          icon,
          value: valueFormatter ? valueFormatter(raw) : normalizeValue(raw),
          toneClass: toneClass(tone)
        }
      }

      return [
        build('DKN_LMS_ALR', 'Alarmy', 'bi bi-exclamation-triangle', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return n > 0 ? `${Math.round(n)}` : 'Brak'
        }),
        build('DKN_LMS_DFS', 'Odszranianie', 'bi bi-snow2', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return n > 0 ? 'Aktywne' : 'Nie'
        }),
        build('DKN_LMS_VSR', 'Sprężarka', 'bi bi-fan', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return `${normalizeValue(raw)} Hz`
        }),
        build('DKN_LMS_SH', 'Przegrzanie', 'bi bi-activity', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return `${normalizeValue(raw)} K`
        }),
        build('DKN_LMS_PPU', 'Zawór', 'bi bi-sliders', (raw) => {
          const n = toNumber(raw)
          if (n === null) return '—'
          return `${normalizeValue(raw)} %`
        })
      ]
    },
    items(): Array<{ key: string; label: string; value: string; toneClass: string }> {
      return Object.values(this.dknParamsByName)
        .filter((param: any) => {
          const name = param?.can_params_io?.param_name
          return typeof name === 'string' ? !QUICK_STATUS_KEYS.has(name) : true
        })
        .map((param: any) => {
          const name = param?.can_params_io?.param_name || '—'
          const description = param?.can_params_io?.description
          const unit = normalizeUnit(extractUnit(description))
          const translationKey = `components.assetDetails.dknLabels.${name}`
          const translated = this.$t(translationKey) as string
          const labelSource = translated && translated !== translationKey ? translated : (stripUnit(description) || name)
          const label = stripUnit(labelSource) || name
          const value = normalizeValue(param?.value)
          const tone = getParamTone(name, param?.value)
          return {
            key: `${name}-${param?.id ?? ''}`,
            label,
            value: unit ? `${value} ${unit}` : value,
            toneClass: toneClass(tone)
          }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    }
  }
})
</script>

<style scoped lang="scss">
.dkn-admin-statuses {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.dkn-admin-status,
.dkn-param-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-left: 4px solid rgba(148, 163, 184, 0.65);
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.55);
}

.dkn-admin-status-header,
.asset-stat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.dkn-admin-status-value,
.asset-stat-value {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.asset-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
}

.asset-stat-header i {
  color: #64748b;
}

.dkn-tone-ok {
  border-left-color: rgba(22, 163, 74, 0.9) !important;
}

.dkn-tone-ok .dkn-admin-status-value,
.dkn-tone-ok .asset-stat-value {
  color: #166534;
}

.dkn-tone-warn {
  border-left-color: rgba(234, 179, 8, 0.95) !important;
}

.dkn-tone-warn .dkn-admin-status-value,
.dkn-tone-warn .asset-stat-value {
  color: #92400e;
}

.dkn-tone-bad {
  border-left-color: rgba(220, 38, 38, 0.95) !important;
}

.dkn-tone-bad .dkn-admin-status-value,
.dkn-tone-bad .asset-stat-value {
  color: #991b1b;
}
</style>
